import { useEffect, useState } from "react";
import { AlertCircle, Check, CheckCircle2, Copy, X, } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import { fetchhealthProgressNotesByAppointment, finalizeHealthProgressNotes, patchHealthProgressNotes, } from "../../api/healthProgressNotes";

import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import LoadingCard from "./LoadingCard";
import PrintExportHeader from "../ui/PrintExportHeader";

const HEALTH_NOTES_POLL_INTERVAL = 5000;
const HEALTH_NOTES_MAX_WAIT = 60000;

const NARRATIVE_FIELDS = [
    "focus_of_session",
    "client_presentation",
    "interventions",
    "client_response",
    "progress",
    "plan_next_steps",
    "safety_risk",
];

const narrativeLabels = {
    focus_of_session: "Focus of Session",
    client_presentation: "Client Presentation",
    interventions: "Interventions",
    client_response: "Client Response",
    progress: "Progress",
    plan_next_steps: "Plan / Next Steps",
    safety_risk: "Safety / Risk",
};

const SAFETY_RISK_EMPTY_TEXT = "Safety/risk was not discussed or documented during this encounter.";

const getRecord = (response) => {
    if (
        response?.data &&
        typeof response.data === "object" &&
        (
            response.data.narrative ||
            response.data.metadata ||
            response.data.status
        )
    ) {
        return response.data;
    }

    return response || {};
};

const getNarrative = (response) => {
    const record = getRecord(response);

    return record?.narrative &&
        typeof record.narrative === "object"
        ? record.narrative
        : {};
};

const getEditableNarrative = (value = {}) =>
    NARRATIVE_FIELDS.reduce((result, field) => {
        result[field] =
            typeof value[field] === "string"
                ? value[field]
                : "";

        return result;
    }, {});

const isMeaningfulNarrative = (value = {}) =>
    NARRATIVE_FIELDS.some(
        (field) =>
            typeof value[field] === "string" &&
            value[field].trim() !== ""
    );

const formatFinalizedDate = (value) => {
    if (!value) return "";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
};

const hasDocumentedValue = (value) =>
    value !== null &&
    value !== undefined &&
    String(value).trim() !== "" &&
    !/^not documented\.?$/i.test(String(value).trim());

const FinalizeConfirmationModal = ({
    onCancel,
    onConfirm,
    isFinalizing,
}) => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
        <div className="relative w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 shadow-xl">
            <button
                type="button"
                onClick={onCancel}
                disabled={isFinalizing}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                aria-label="Close finalize confirmation"
            >
                <X className="h-4 w-4" />
            </button>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">
                Finalize Behavioral Health Progress Note
            </h3>

            <p className="text-sm leading-relaxed text-gray-600">
                Are you sure you want to finalize this note?
                Once finalized, it cannot be edited.
            </p>

            <div className="mt-6 flex justify-end gap-3">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={isFinalizing}
                >
                    Cancel
                </Button>

                <Button
                    type="button"
                    onClick={onConfirm}
                    disabled={isFinalizing}
                    className="bg-blue-600 text-white hover:bg-blue-700"
                >
                    {isFinalizing
                        ? "Finalizing..."
                        : "Confirm Finalize"}
                </Button>
            </div>
        </div>
    </div>
);

const HealthProgressNotes = ({
    appointmentId,
    username,
    duration = "",
    canEdit = true,
    canPostToAthena = false,
}) => {
    const { toast } = useToast();

    const [record, setRecord] = useState(null);
    const [narrative, setNarrative] = useState({});
    const [isEditing, setIsEditing] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isFinalizing, setIsFinalizing] = useState(false);

    const [isConfirmingFinalize, setIsConfirmingFinalize] =
        useState(false);

    const [error, setError] = useState("");
    const [hasTimedOut, setHasTimedOut] = useState(false);

    const [retryKey, setRetryKey] = useState(0);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [copiedField, setCopiedField] = useState("");

    useEffect(() => {
        let isMounted = true;
        let timeoutId = null;

        const deadline =
            Date.now() + HEALTH_NOTES_MAX_WAIT;

        const pollForNotes = async () => {
            try {
                const response =
                    await fetchhealthProgressNotesByAppointment(
                        appointmentId,
                        username
                    );

                const nextRecord = getRecord(response);
                const nextNarrative =
                    getEditableNarrative(
                        getNarrative(nextRecord)
                    );

                if (
                    isMounted &&
                    isMeaningfulNarrative(nextNarrative)
                ) {
                    setRecord(nextRecord);
                    setNarrative(nextNarrative);
                    setIsLoading(false);
                    return;
                }
            } catch (pollError) {
                /*
                 * 404 means the note is not generated yet,
                 * so continue polling until the 60-second limit.
                 *
                 * Other errors should not silently become
                 * "note not available" because they may be auth
                 * or permission failures.
                 */
                if (pollError?.status !== 404) {
                    if (!isMounted) return;

                    setIsLoading(false);
                    setError(
                        pollError?.message ||
                        "Failed to load Behavioral Health Progress Note."
                    );

                    toast({
                        title: "Behavioral Health Progress Note",
                        description:
                            pollError?.message ||
                            "Failed to load the note.",
                        variant: "destructive",
                    });

                    return;
                }
            }

            if (!isMounted) return;

            const remainingWait =
                deadline - Date.now();

            if (remainingWait <= 0) {
                setIsLoading(false);
                setHasTimedOut(true);
                return;
            }

            timeoutId = setTimeout(
                pollForNotes,
                Math.min(
                    HEALTH_NOTES_POLL_INTERVAL,
                    remainingWait
                )
            );
        };

        setIsLoading(true);
        setHasTimedOut(false);
        setError("");
        setRecord(null);
        setNarrative({});

        if (appointmentId && username) {
            pollForNotes();
        } else {
            setIsLoading(false);
            setHasTimedOut(true);
        }

        return () => {
            isMounted = false;

            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [appointmentId, username, retryKey, toast]);

    const updateNarrative = (field, value) => {
        setNarrative((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const handleSave = async () => {
        if (!record || isSaving || isFinalizing) return;

        setIsSaving(true);
        setError("");
        setSaveSuccess(false);

        const payload =
            getEditableNarrative(narrative);

        try {
            const response =
                await patchHealthProgressNotes(
                    appointmentId,
                    username,
                    payload
                );

            const savedRecord = getRecord(response);

            setRecord(savedRecord);
            setNarrative(
                getEditableNarrative(
                    getNarrative(savedRecord)
                )
            );

            setIsEditing(false);
            setSaveSuccess(true);

            toast({
                title: "Behavioral Health Progress Note",
                description:
                    "Progress note saved successfully.",
            });

            window.setTimeout(() => {
                setSaveSuccess(false);
            }, 5000);
        } catch (saveError) {
            setError(
                saveError?.message ||
                "Failed to save Behavioral Health Progress Note."
            );

            toast({
                title: "Failed to save Progress Note",
                description:
                    saveError?.message ||
                    "The progress note could not be saved.",
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleFinalize = async () => {
        if (
            !record ||
            isFinalizing ||
            isSaving ||
            isFinalized
        ) {
            return;
        }

        setIsFinalizing(true);
        setError("");
        setSaveSuccess(false);

        try {
            const response =
                await finalizeHealthProgressNotes(
                    appointmentId,
                    username
                );

            const finalizedRecord =
                getRecord(response);

            setRecord(finalizedRecord);

            setNarrative(
                getEditableNarrative(
                    getNarrative(finalizedRecord)
                )
            );

            setIsEditing(false);
            setIsConfirmingFinalize(false);

            toast({
                title: "Behavioral Health Progress Note",
                description:
                    "Progress note finalized successfully.",
            });
        } catch (finalizeError) {
            setError(
                finalizeError?.message ||
                "Failed to finalize Behavioral Health Progress Note."
            );

            toast({
                title: "Failed to finalize Progress Note",
                description:
                    finalizeError?.message ||
                    "The progress note could not be finalized.",
                variant: "destructive",
            });
        } finally {
            setIsFinalizing(false);
        }
    };

    const handleRetry = () => {
        setRetryKey((current) => current + 1);
    };

    const handleCopy = async (field) => {
        const label =
            narrativeLabels[field] || field;

        const text =
            typeof narrative[field] === "string"
                ? narrative[field]
                : "";

        if (!text.trim()) {
            toast({
                title: "Nothing to copy",
                description: `${label} has no content.`,
            });

            return;
        }

        try {
            await navigator.clipboard.writeText(text);

            setCopiedField(field);

            toast({
                title: "Copied",
                description: `${label} copied to clipboard.`,
            });

            window.setTimeout(() => {
                setCopiedField((current) =>
                    current === field ? "" : current
                );
            }, 1500);
        } catch {
            toast({
                title: "Copy failed",
                description:
                    "The section could not be copied.",
                variant: "destructive",
            });
        }
    };

    if (isLoading) {
        return (
            <LoadingCard
                message={
                    "Behavioral Health Progress Note is still being prepared..."
                }
            />
        );
    }

    if (hasTimedOut && !record) {
        return (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                <p className="text-sm font-medium text-slate-700">
                    Behavioral Health Progress Notes are not available for this appointment yet.
                </p>

                <Button
                    type="button"
                    onClick={handleRetry}
                    className="mt-4 bg-blue-600 text-white hover:bg-blue-700"
                >
                    Retry
                </Button>
            </div>
        );
    }

    if (error && !record) {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>{error}</span>
                </div>

                <Button
                    type="button"
                    variant="outline"
                    onClick={handleRetry}
                    className="mt-4"
                >
                    Retry
                </Button>
            </div>
        );
    }

    const metadata =
        record?.metadata || {};

    const clinician =
        metadata?.clinician || {};

    const clinicianDisplay = [
        clinician.name,
        clinician.credentials,
    ]
        .filter(Boolean)
        .join(", ");

    const nextAppointment =
        metadata.next_appointment;

    const noteStatus =
        record?.status || "draft";

    const normalizedStatus =
        String(noteStatus).toLowerCase();

    const isFinalized =
        normalizedStatus === "finalized";

    const finalizedBy =
        record?.finalized_by || "";

    const finalizedAt =
        record?.finalized_at || "";

    const finalizedDate =
        formatFinalizedDate(finalizedAt);

    const sessionDuration = hasDocumentedValue(
        metadata.duration
    )
        ? metadata.duration
        : duration;

    const headerFields = [
        ["Date", metadata.date],
        ["Patient", metadata.client_name],
        ["Session Type", metadata.session_type],
        ["Duration", sessionDuration],
        [
            "Location / Modality",
            metadata.location_modality,
        ],
    ];

    return (
        <div className="relative space-y-6 text-gray-900">
            {isConfirmingFinalize && (
                <FinalizeConfirmationModal
                    onCancel={() =>
                        setIsConfirmingFinalize(false)
                    }
                    onConfirm={handleFinalize}
                    isFinalizing={isFinalizing}
                />
            )}

            <PrintExportHeader
                title="Practice Intelligence Behavioral Health Progress Note"
                subtitle="Clinical Progress Note"
            />

            {/* MAIN NOTE CARD */}
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                {/* HEADER */}
                <div className="border-b border-slate-200 p-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900">
                                Behavioral Health Progress Note
                            </h3>

                            <p className="mt-1 text-sm text-slate-500">
                                Behavioral health session narrative
                            </p>

                            {isFinalized && (
                                <p className="mt-2 text-xs font-medium text-slate-500">
                                    Finalized by{" "}
                                    <span className="text-slate-700">
                                        {finalizedBy ||
                                            "Unknown user"}
                                    </span>

                                    {finalizedDate && (
                                        <>
                                            {" "}
                                            on{" "}
                                            <span className="text-slate-700">
                                                {finalizedDate}
                                            </span>
                                        </>
                                    )}
                                </p>
                            )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            {isEditing && (
                                <span className="rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                                    Editing Mode
                                </span>
                            )}

                            <span
                                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium capitalize ${isFinalized
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                        : "border-slate-200 bg-slate-100 text-slate-600"
                                    }`}
                            >
                                <span
                                    className={`h-1.5 w-1.5 rounded-full ${isFinalized
                                            ? "bg-emerald-500"
                                            : "bg-slate-400"
                                        }`}
                                />

                                {isFinalized
                                    ? "Finalized"
                                    : "Draft"}
                            </span>
                        </div>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        {headerFields.map(
                            ([label, value]) => (
                                <div
                                    key={label}
                                    className="min-w-0"
                                >
                                    <p className="text-xs font-semibold text-slate-500">
                                        {label}
                                    </p>

                                    <p className="mt-1 break-words text-sm text-slate-700">
                                        {value === null ||
                                            value === undefined ||
                                            value === ""
                                            ? "Not documented."
                                            : value}
                                    </p>
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* BODY */}
                <div className="p-6">
                    <div className="space-y-6">
                        {NARRATIVE_FIELDS.map(
                            (field) => {
                                const value =
                                    narrative[field] ||
                                    "";

                                return (
                                    <section
                                        key={field}
                                        className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <h4 className="text-base font-semibold text-slate-900">
                                                {
                                                    narrativeLabels[
                                                    field
                                                    ]
                                                }
                                            </h4>

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleCopy(
                                                        field
                                                    )
                                                }
                                                className={`inline-flex h-7 shrink-0 items-center justify-center rounded-full border transition-all whitespace-nowrap ${copiedField ===
                                                        field
                                                        ? "gap-1.5 border-green-300 bg-green-50 px-2 text-green-700"
                                                        : "w-7 border-blue-200 bg-white text-blue-500 hover:border-blue-400 hover:text-blue-700"
                                                    }`}
                                                title={`Copy ${narrativeLabels[field]}`}
                                            >
                                                {copiedField ===
                                                    field ? (
                                                    <Check className="h-3.5 w-3.5" />
                                                ) : (
                                                    <Copy className="h-3.5 w-3.5" />
                                                )}

                                                {copiedField ===
                                                    field
                                                    && (
                                                        <span className="text-[10px] font-semibold uppercase tracking-wide">
                                                            Copied
                                                        </span>
                                                    )}
                                            </button>
                                        </div>

                                        <div className="mt-3">
                                            {isEditing ? (
                                                <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-2">
                                                    <Textarea
                                                        value={
                                                            value
                                                        }
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            updateNarrative(
                                                                field,
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        rows={5}
                                                        placeholder={`Enter ${narrativeLabels[field].toLowerCase()}...`}
                                                        className="border-0 bg-transparent shadow-none focus-visible:ring-0"
                                                    />
                                                </div>
                                            ) : (
                                                <p className="text-sm leading-relaxed whitespace-pre-line text-slate-700">
                                                    {value}
                                                </p>
                                            )}
                                        </div>

                                        {field ===
                                            "safety_risk" &&
                                            value ===
                                            SAFETY_RISK_EMPTY_TEXT && (
                                                <span className="sr-only">
                                                    Safety / Risk not otherwise documented.
                                                </span>
                                            )}
                                    </section>
                                );
                            }
                        )}
                    </div>
                </div>

                {/* FOOTER */}
                <div className="border-t border-slate-200 p-6">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <p className="text-xs font-semibold text-slate-500">
                                Next Appointment
                            </p>

                            <p className="mt-1 text-sm text-slate-700">
                                {nextAppointment
                                    ? nextAppointment
                                    : "Not scheduled/documented."}
                            </p>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-slate-500">
                                Doctor
                            </p>

                            <p className="mt-1 text-sm text-slate-700">
                                {clinicianDisplay ||
                                    "Not documented."}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ACTION ROW — SOAP-STYLE POSITION */}
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-300 pt-4">
                {saveSuccess && (
                    <span className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                        <CheckCircle2 className="h-4 w-4" />
                        Saved
                    </span>
                )}

                {!isEditing ? (
                    <>
                        {canEdit && (
                            <Button
                                type="button"
                                onClick={() => {
                                    setError("");
                                    setSaveSuccess(false);

                                    setNarrative(
                                        getEditableNarrative(
                                            getNarrative(record)
                                        )
                                    );

                                    setIsEditing(true);
                                }}
                                disabled={isFinalized}
                                className="rounded-lg bg-amber-600 px-5 text-white shadow-sm hover:bg-amber-700"
                            >
                                Edit
                            </Button>
                        )}

                        {canPostToAthena && (
                            <Button
                                type="button"
                                onClick={() =>
                                    setIsConfirmingFinalize(
                                        true
                                    )
                                }
                                disabled={
                                    isFinalized ||
                                    isFinalizing
                                }
                                className={
                                    isFinalized
                                        ? "cursor-not-allowed bg-gray-400 text-white hover:bg-gray-400"
                                        : "bg-blue-600 text-white hover:bg-blue-700"
                                }
                            >
                                {/* <CheckCircle2 className="mr-2 h-4 w-4" /> */}
                                Finalize
                            </Button>
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {isSaving
                                ? "Saving..."
                                : "Save Changes"}
                        </Button>

                        <Button
                            type="button"
                            onClick={() => {
                                setNarrative(
                                    getEditableNarrative(
                                        getNarrative(record)
                                    )
                                );

                                setIsEditing(false);
                                setError("");
                            }}
                            disabled={isSaving}
                            className="bg-gray-500 text-white hover:bg-gray-600"
                        >
                            Cancel
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default HealthProgressNotes;